import React from "react";
import { Box, Container, Typography, Link, useTheme } from "@mui/material";
import { useWhitelabel } from "../context/WhitelabelContext";

interface BrandedPageFooterProps {
  currentPage?: "pricing" | "security" | "roadmap" | "privacy" | "terms" | "login" | "status" | "training";
}

const BrandedPageFooter: React.FC<BrandedPageFooterProps> = ({ currentPage }) => {
  const theme = useTheme();
  const { whitelabelSettings } = useWhitelabel();
  const appName = whitelabelSettings?.title || "GTM AI";

  // Hide pricing link when whitelabel has custom subscription handling
  const showPricing = !whitelabelSettings?.customSubscriptionEnabled;

  const mainLinks = [
    ...(showPricing ? [{ href: "/pricing", label: "Pricing", key: "pricing" }] : []),
    { href: "/security", label: "Security", key: "security" },
    { href: "/status", label: "Status", key: "status" },
    { href: "/roadmap", label: "Roadmap", key: "roadmap" },
    { href: "/login", label: "Login", key: "login" },
  ];

  const legalLinks = [
    { href: "/privacy", label: "Privacy", key: "privacy" },
    { href: "/terms", label: "Terms", key: "terms" },
  ];

  const LinkItem: React.FC<{ href: string; label: string; linkKey: string }> = ({
    href,
    label,
    linkKey,
  }) => (
    <Link
      href={href}
      sx={{
        color: currentPage === linkKey ? "primary.main" : "text.secondary",
        textDecoration: "none",
        fontWeight: currentPage === linkKey ? 600 : 400,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {label}
    </Link>
  );

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? "background.paper" : "grey.100",
        py: 4,
        mt: "auto",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "flex-start" },
            gap: 2,
            mb: 2,
          }}
        >
          {/* Main Links - Left Side */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            {mainLinks.map((link) => (
              <LinkItem
                key={link.key}
                href={link.href}
                label={link.label}
                linkKey={link.key}
              />
            ))}
          </Box>

          {/* Legal Links - Right Side */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: { xs: "center", sm: "flex-end" },
            }}
          >
            {legalLinks.map((link) => (
              <LinkItem
                key={link.key}
                href={link.href}
                label={link.label}
                linkKey={link.key}
              />
            ))}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: { xs: "center", sm: "left" } }}
        >
          &copy; {new Date().getFullYear()} {appName}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default BrandedPageFooter;
